import { formatDistanceToNow } from "date-fns";
import { UploadSimple, ChatCircleText, CheckCircle, WarningCircle, Eye, Fire, Clock, Key, LockKeyOpen, ProhibitInset, UserMinus } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { RepSessionClock } from "./RepSessionClock";

const STATUS_META = {
  green: { border: "border-igc-success/40", dot: "bg-igc-success", label: "Active" },
  yellow: { border: "border-amber-500/50", dot: "bg-amber-500", label: "Slowing" },
  red: { border: "border-igc-danger/60", dot: "bg-igc-danger", label: "Inactive" },
};

const STATUS_EXPLAIN = {
  green: "This rep has been active recently (logging calls or opening leads). Green means they're keeping up a healthy pace.",
  yellow: "This rep's activity has slowed down compared to usual. Not an emergency, but worth a friendly check-in.",
  red: "This rep hasn't logged any activity in a while. Worth reaching out to see if they need help or leads.",
};

export function RepCard({ rep, onOpen, onUpload, onMessage, onViewAs, onResetPassword, onUnlock, onResetLeadCount, onWholesaleReplace, onClearLastLogin, onClearCallsAttempted, onToggleBlockLeads, onToggleAutoAssignExclude }) {
  const status = STATUS_META[rep.activity_status] || STATUS_META.yellow;
  const trustLow = rep.trust_remaining <= 35;
  const fundingLow = rep.funding_remaining <= 35;
  const freshness = rep.queue_freshness || {};
  const loadDates = [freshness.trust?.last_loaded_at, freshness.funding?.last_loaded_at].filter(Boolean).map((d) => new Date(d));
  const lastBatchLoadedAt = loadDates.length ? new Date(Math.max(...loadDates)) : null;
  const replenishDates = [freshness.trust?.last_replenishment_at, freshness.funding?.last_replenishment_at].filter(Boolean).map((d) => new Date(d));
  const lastReplenishmentAt = replenishDates.length ? new Date(Math.max(...replenishDates)) : null;

  return (
    <div
      data-testid={`rep-card-${rep.id}`}
      title="Click anywhere on this card (outside a button) to open this rep's full profile and activity history."
      className={`flex-shrink-0 w-72 min-h-[600px] bg-igc-surface border ${status.border} p-5 flex flex-col justify-between cursor-pointer hover:border-igc-gold/40 transition-colors`}
      onClick={() => onOpen(rep)}
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-igc-cream text-sm font-medium truncate min-w-0" data-testid={`rep-card-name-${rep.id}`} title="This rep's full name.">{rep.display_name}</p>
          <span className="flex items-center gap-1.5 shrink-0" data-testid={`rep-card-indicator-${rep.id}`} title={STATUS_EXPLAIN[rep.activity_status] || STATUS_EXPLAIN.yellow}>
            <span className={`w-2.5 h-2.5 rounded-full ${status.dot}`} />
            <span className="text-igc-muted text-[10px] uppercase tracking-wide">{status.label}</span>
          </span>
        </div>
        {rep.blocked_from_leads && (
          <div className="flex items-center gap-1 mb-2 text-igc-danger" data-testid={`rep-card-blocked-leads-badge-${rep.id}`} title="This rep is blocked from receiving any leads — every future lead upload and reload will skip them automatically until an admin turns this back off.">
            <ProhibitInset size={12} weight="fill" />
            <span className="text-[10px] font-medium uppercase tracking-wide">Blocked From Leads</span>
          </div>
        )}
        {rep.excluded_from_autoassign && (
          <div className="flex items-center gap-1 mb-2 text-amber-500" data-testid={`rep-card-excluded-autoassign-badge-${rep.id}`} title="This rep is skipped by the HIGH-tier website-visitor auto-assign rotation. Account, existing leads, and portal access are untouched.">
            <UserMinus size={12} weight="fill" />
            <span className="text-[10px] font-medium uppercase tracking-wide">Excluded From Auto-Assign</span>
          </div>
        )}
        {rep.calling_streak_days >= 3 && (
          <div className="flex items-center gap-1 mb-2 text-amber-600" data-testid={`rep-card-streak-${rep.id}`} title="The number of days in a row this rep has made at least one call. A motivational streak counter — no penalty if it breaks, it just starts over.">
            <Fire size={12} weight="fill" />
            <span className="text-[10px] font-medium">{rep.calling_streak_days}-Day Streak</span>
          </div>
        )}
        {rep.over_lead_threshold && (
          <div className="flex items-center gap-1 mb-2 text-igc-danger" data-testid={`rep-card-over-threshold-${rep.id}`} title={`This rep has more open leads than their allowed limit of ${rep.lead_upload_threshold}. Consider holding off on loading them more leads until they work through their backlog.`}>
            <WarningCircle size={12} weight="fill" />
            <span className="text-[10px] font-medium uppercase tracking-wide">Over Lead Threshold ({rep.lead_upload_threshold})</span>
          </div>
        )}
        {rep.skipped_this_week_count >= 3 && (
          <div className="flex items-center gap-1 mb-2 text-amber-600" data-testid={`rep-card-skip-trend-${rep.id}`} title="How many times this week this rep dispositioned a lead as 'Not Interested' or skipped it without a real attempt. A high number here can mean the rep needs coaching on working leads properly.">
            <WarningCircle size={12} weight="fill" />
            <span className="text-[10px] font-medium uppercase tracking-wide">Skipped {rep.skipped_this_week_count}x this week</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-igc-goldText text-xs truncate min-w-0" title="This rep's unique rep code — used to identify them in referral links, lead assignment, and commission tracking.">{rep.user_id}</p>
          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 shrink-0 ${rep.tier === "full_partner" ? "bg-igc-success/10 text-igc-success" : "bg-igc-gold/15 text-igc-goldText"}`} data-testid={`rep-card-tier-${rep.id}`} title="Starter reps earn a lower commission rate on deals. Full Partner reps (usually promoted after enough time/deals) earn a higher rate on every deal going forward.">
            {rep.tier === "full_partner" ? "Full Partner" : "Starter"}
          </span>
        </div>
        <div className="flex items-center justify-between mb-1">
          <span className={`flex items-center gap-1 text-[10px] uppercase tracking-wide ${rep.w9_on_file ? "text-igc-success" : "text-amber-600"}`} data-testid={`rep-card-w9-status-${rep.id}`} title="Whether this rep's W-9 tax form is on file. Without it on file, IGC cannot legally pay this rep their commission.">
            {rep.w9_on_file ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />}
            {rep.w9_on_file ? "W-9 On File" : "W-9 Pending"}
          </span>
          <span className="text-igc-muted text-[10px] uppercase tracking-wide" data-testid={`rep-card-score-${rep.id}`} title="An overall performance score combining this rep's call activity, contact rate, and deals closed — higher is better.">Score: {rep.performance_score}</span>
        </div>
        <p className="text-igc-muted text-xs mb-3 break-words" title="Which manager this rep reports to, and (if applicable) how many days are left in their initial ramp-up period.">
          Manager: {rep.manager_display_name || "Unassigned"}
          {rep.days_remaining_30day != null && ` · Clock: ${rep.days_remaining_30day}d left`}
        </p>
        <p className="text-igc-muted text-[11px] mb-3" data-testid={`rep-card-timezone-${rep.id}`} title="This rep's time zone and the hours they've told Nia they're generally available to take calls.">
          {rep.time_zone || "Eastern"}{rep.availability_hours ? ` · Available: ${rep.availability_hours}` : ""}
        </p>

        <div className="bg-igc-surfaceLight border border-igc-gold/10 px-3 py-2 mb-3">
          <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1" title="Shows whether this rep is currently logged into the portal right now, and for how long.">
            <Clock size={11} weight="bold" /> Session
          </p>
          <RepSessionClock session={rep.session} testId={`rep-card-session-${rep.id}`} />
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
          <div data-testid={`rep-card-remaining-trust-${rep.id}`} title="How many Trust & Asset Protection leads this rep has left to work through, out of the total loaded into their folder. Turns amber when running low.">
            <p className={`font-serif text-lg ${trustLow ? "text-amber-600" : "text-igc-cream"}`}>{rep.trust_remaining}/{rep.trust_total}</p>
            <p className="text-igc-muted uppercase tracking-widest">Trust Left</p>
          </div>
          <div data-testid={`rep-card-remaining-funding-${rep.id}`} title="How many Business Funding leads this rep has left to work through, out of the total loaded into their folder. Turns amber when running low.">
            <p className={`font-serif text-lg ${fundingLow ? "text-amber-600" : "text-igc-cream"}`}>{rep.funding_remaining}/{rep.funding_total}</p>
            <p className="text-igc-muted uppercase tracking-widest">Funding Left</p>
          </div>
          <div data-testid={`rep-card-calls-today-${rep.id}`} title="How many times this rep has clicked Call today, across every lead. Resets to 0 at midnight.">
            <p className="font-serif text-lg text-igc-cream">{rep.calls_attempted_today}</p>
            <p className="text-igc-muted uppercase tracking-widest">Calls Today</p>
          </div>
          <div data-testid={`rep-card-opened-today-${rep.id}`} title="How many different lead cards this rep has clicked open/viewed today — a sign of how actively they're working their queue.">
            <p className="font-serif text-lg text-igc-cream">{rep.leads_opened_today}</p>
            <p className="text-igc-muted uppercase tracking-widest">Opened Today</p>
          </div>
          <div data-testid={`rep-card-referrals-${rep.id}`} title="How many deals (trust or funding) this rep has submitted to IGC in the last 7 days.">
            <p className="font-serif text-lg text-igc-cream">{rep.referrals_submitted_week}</p>
            <p className="text-igc-muted uppercase tracking-widest">Referrals (Wk)</p>
          </div>
          <div data-testid={`rep-card-deals-${rep.id}`} title="The total number of deals this rep has personally submitted to IGC, all time (not counting leads assigned to them by the system).">
            <p className="font-serif text-lg text-igc-cream">{rep.deals_submitted}</p>
            <p className="text-igc-muted uppercase tracking-widest">Personal Referrals</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs mb-3 bg-igc-surfaceLight border border-igc-gold/10 px-3 py-2" data-testid={`rep-card-grading-${rep.id}`}>
          <div data-testid={`rep-card-calls-remaining-${rep.id}`} title="Leads assigned to this rep that have never been called even once yet.">
            <p className="font-serif text-lg text-igc-cream">{rep.calls_remaining}</p>
            <p className="text-igc-muted uppercase tracking-widest">Remaining Calls</p>
          </div>
          <div data-testid={`rep-card-attempted-calls-${rep.id}`} title="Every dial this rep has made, all time — redials to the same lead count each time.">
            <div className="flex items-center gap-1.5">
              <p className="font-serif text-lg text-igc-cream">{rep.calls_attempted_total}</p>
              {onClearCallsAttempted && (
                <button
                  data-testid={`rep-card-clear-calls-attempted-${rep.id}`}
                  onClick={(e) => { e.stopPropagation(); onClearCallsAttempted(rep); }}
                  title="Resets Attempted Calls, Sweep Calls, and Unique Leads Called back to 0 for this rep — logged to the ops activity log."
                  className="text-igc-muted hover:text-igc-danger text-[9px] uppercase tracking-wide underline"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-igc-muted uppercase tracking-widest">Attempted Calls</p>
          </div>
          <div data-testid={`rep-card-sweep-calls-${rep.id}`} title="A Sweep Call is any call made on a lead the rep has already called at least once before.">
            <p className="font-serif text-lg text-igc-cream">{rep.sweep_calls_total}</p>
            <p className="text-igc-muted uppercase tracking-widest">Sweep Calls</p>
          </div>
          <div data-testid={`rep-card-unique-leads-called-${rep.id}`} title="Distinct leads this rep has called at least once, all time.">
            <p className="font-serif text-lg text-igc-cream">{rep.unique_leads_called_alltime}</p>
            <p className="text-igc-muted uppercase tracking-widest">Unique Leads Called</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs mb-3 bg-igc-surfaceLight border border-igc-gold/10 px-3 py-2" data-testid={`rep-card-dispositions-${rep.id}`}>
          <div data-testid={`rep-card-contacts-made-${rep.id}`} title="Leads currently sitting at the Contacted stage — a disposition was logged and it wasn't a No Answer / Left Voicemail / Wrong Number. Live count from the leads collection, every render.">
            <p className="font-serif text-lg text-igc-success">{rep.contacted_count}</p>
            <p className="text-igc-muted uppercase tracking-widest">Contacts Made</p>
          </div>
          <div data-testid={`rep-card-followups-${rep.id}`} title="Pending callbacks this rep has scheduled and hasn't marked done yet.">
            <p className="font-serif text-lg text-igc-cream">{rep.follow_ups_scheduled}</p>
            <p className="text-igc-muted uppercase tracking-widest">Follow-Ups</p>
          </div>
          <div data-testid={`rep-card-not-interested-${rep.id}`} title="Distinct leads this rep has dispositioned as Not Interested, all time.">
            <p className="font-serif text-lg text-igc-cream">{rep.not_interested_count}</p>
            <p className="text-igc-muted uppercase tracking-widest">Not Interested</p>
          </div>
          <div data-testid={`rep-card-deals-closed-${rep.id}`} title="Distinct leads this rep has dispositioned as Deal Closed, all time.">
            <p className="font-serif text-lg text-igc-goldText">{rep.deals_closed_count}</p>
            <p className="text-igc-muted uppercase tracking-widest">Deals Closed</p>
          </div>
        </div>

        <div className="mb-3" data-testid={`rep-card-commissions-${rep.id}`} title="Total commission this rep has earned across every approved deal, all time.">
          <p className="font-serif text-lg text-igc-goldText">${rep.commissions_earned?.toLocaleString?.() ?? rep.commissions_earned}</p>
          <p className="text-igc-muted text-xs uppercase tracking-widest">Commissions Earned</p>
        </div>

        <p className="text-igc-muted text-[11px]" data-testid={`rep-card-last-activity-${rep.id}`} title="The most recent time this rep did anything trackable — a call, a note, a status update.">
          Last activity: {rep.last_activity ? formatDistanceToNow(new Date(rep.last_activity), { addSuffix: true }) : "No activity yet"}
        </p>
        <div className="flex items-center justify-between gap-2">
          <p className="text-igc-muted text-[11px]" data-testid={`rep-card-last-login-${rep.id}`} title="The last time this rep actually signed into the portal.">
            Last login: {rep.last_login_at ? formatDistanceToNow(new Date(rep.last_login_at), { addSuffix: true }) : "Never"}
          </p>
          {onClearLastLogin && (
            <button
              data-testid={`rep-card-clear-last-login-${rep.id}`}
              onClick={(e) => { e.stopPropagation(); onClearLastLogin(rep); }}
              title="Resets the Last Login stat back to 'Never' for this rep — logged to the ops activity log."
              className="text-igc-muted hover:text-igc-danger text-[10px] uppercase tracking-wide underline shrink-0"
            >
              Clear
            </button>
          )}
        </div>
        {lastBatchLoadedAt && (
          <p className="text-igc-muted text-[11px]" data-testid={`rep-card-last-batch-loaded-${rep.id}`} title="The last time IGC's team manually loaded a fresh batch of leads into this rep's folder.">
            Last batch loaded: {formatDistanceToNow(lastBatchLoadedAt, { addSuffix: true })}
          </p>
        )}
        {lastReplenishmentAt && (
          <p className="text-igc-success text-[11px]" data-testid={`rep-card-last-replenishment-${rep.id}`} title="The last time the system automatically topped up this rep's lead folder on its own because they were running low.">
            Last auto top-up: {formatDistanceToNow(lastReplenishmentAt, { addSuffix: true })}
          </p>
        )}
      </div>

      <div className="flex gap-2 mt-4">
        {onMessage && (
          <Button
            data-testid={`rep-card-message-button-${rep.id}`}
            onClick={(e) => { e.stopPropagation(); onMessage(rep); }}
            title="Opens a box to send this rep a direct message inside the portal — they'll see it as a notification."
            className="flex-1 h-8 text-xs bg-igc-surfaceLight hover:bg-igc-gold/10 border border-igc-gold/30 text-igc-goldText flex items-center justify-center gap-1.5"
          >
            <ChatCircleText size={13} weight="bold" /> Message
          </Button>
        )}
        {onViewAs && (
          <Button
            data-testid={`rep-card-view-as-button-${rep.id}`}
            onClick={(e) => { e.stopPropagation(); onViewAs(rep); }}
            className="flex-1 h-8 text-xs bg-igc-surfaceLight hover:bg-igc-gold/10 border border-igc-gold/30 text-igc-goldText flex items-center justify-center gap-1.5"
            title="Lets you see exactly what this rep sees in their own dashboard, read-only — you cannot make changes while viewing as them. Useful for troubleshooting what a rep is describing."
          >
            <Eye size={13} weight="bold" /> View As
          </Button>
        )}
        {onUpload && (
          <Button
            data-testid={`rep-card-upload-button-${rep.id}`}
            onClick={(e) => { e.stopPropagation(); onUpload(rep); }}
            title="Opens the tool to manually load a new batch of leads directly into this rep's folder."
            className="flex-1 h-8 text-xs bg-igc-gold hover:bg-igc-goldHover text-black flex items-center justify-center gap-1.5"
          >
            <UploadSimple size={13} weight="bold" /> Upload Leads
          </Button>
        )}
      </div>
      {(onResetPassword || onUnlock || onResetLeadCount || onWholesaleReplace) && (
        <div className="flex gap-2 mt-2">
          {onResetPassword && (
            <Button
              data-testid={`rep-card-reset-password-button-${rep.id}`}
              onClick={(e) => { e.stopPropagation(); onResetPassword(rep); }}
              title="Generates a brand-new temporary password for this rep and shows it to you to share with them — use this when a rep forgets their password and can't reset it themselves."
              className="flex-1 h-7 text-[11px] bg-igc-surfaceLight hover:bg-igc-gold/10 border border-igc-gold/30 text-igc-goldText flex items-center justify-center gap-1"
            >
              <Key size={12} weight="bold" /> Reset Password
            </Button>
          )}
          {onUnlock && (
            <Button
              data-testid={`rep-card-unlock-button-${rep.id}`}
              onClick={(e) => { e.stopPropagation(); onUnlock(rep); }}
              title="This clears a rep's failed login lockout. Use this when a rep is locked out after too many wrong password attempts. It has nothing to do with activating or deactivating the account."
              className="flex-1 h-7 text-[11px] bg-igc-surfaceLight hover:bg-igc-gold/10 border border-igc-gold/30 text-igc-goldText flex items-center justify-center gap-1"
            >
              <LockKeyOpen size={12} weight="bold" /> Clear Login Lockout
            </Button>
          )}
          {onResetLeadCount && (
            <Button
              data-testid={`rep-card-reset-lead-count-button-${rep.id}`}
              onClick={(e) => { e.stopPropagation(); onResetLeadCount(rep); }}
              title="Trims an over-inflated lead count down to a standard target — use this when a rep somehow ended up with way more leads in their folder than they should have."
              className="flex-1 h-7 text-[11px] bg-igc-surfaceLight hover:bg-igc-gold/10 border border-igc-gold/30 text-igc-goldText flex items-center justify-center gap-1"
            >
              <WarningCircle size={12} weight="bold" /> Reset Lead Count
            </Button>
          )}
        </div>
      )}
      {onWholesaleReplace && (
        <div className="flex gap-2 mt-2">
          <Button
            data-testid={`rep-card-wholesale-replace-button-${rep.id}`}
            onClick={(e) => { e.stopPropagation(); onWholesaleReplace(rep); }}
            title="Replaces EVERY lead this rep has in one category, even ones they've already called, with a fresh batch. Use this only when a rep reports genuinely bad data across the board despite the leads passing our normal verification — it's a heavier reset than Upload Leads."
            className="flex-1 h-7 text-[11px] bg-igc-danger/10 hover:bg-igc-danger/20 border border-igc-danger/40 text-igc-danger flex items-center justify-center gap-1"
          >
            <Fire size={12} weight="bold" /> Replace All Leads
          </Button>
        </div>
      )}
      {onToggleBlockLeads && rep.user_id !== "WN071" && (
        <div
          className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-igc-gold/10"
          onClick={(e) => e.stopPropagation()}
          title="When on, this rep is excluded from every future lead upload and reload — the system silently skips them and moves on to the next rep, no errors."
        >
          <span className="text-igc-muted text-[10px] uppercase tracking-wide">Block Lead Uploads</span>
          <Switch
            data-testid={`rep-card-block-leads-toggle-${rep.id}`}
            checked={!!rep.blocked_from_leads}
            onCheckedChange={(checked) => onToggleBlockLeads(rep, checked)}
          />
        </div>
      )}
      {onToggleAutoAssignExclude && (
        <div
          className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-igc-gold/10"
          onClick={(e) => e.stopPropagation()}
          title="When on, this rep is skipped by the HIGH-tier website-visitor auto-assign rotation. Their account, existing leads, and portal access are untouched — toggle off anytime to re-include them."
        >
          <span className="text-igc-muted text-[10px] uppercase tracking-wide">Exclude from Auto-Assign</span>
          <Switch
            data-testid={`rep-card-exclude-autoassign-toggle-${rep.id}`}
            checked={!!rep.excluded_from_autoassign}
            onCheckedChange={(checked) => onToggleAutoAssignExclude(rep, checked)}
          />
        </div>
      )}
    </div>
  );
}
